import { acceptHMRUpdate, defineStore } from 'pinia';
import type {
  BillingCartAddressFragment,
  CartFragment,
  CartItemFragment,
  CommonProductFragment,
  CustomerOrder,
  Money,
  OrderAddress,
  OrderTotal,
  PlaceQuoteInput,
  ProductDetailFragment,
  ProductInterface,
  SelectedPaymentMethod,
  SelectedShippingMethodFragment,
  ShippingCartAddressFragment
} from '~~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';
import {
  filterAppliedTaxRows,
  sumAppliedTaxAmounts
} from '~/utils/shop/appliedTaxRows';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import {
  G4_EVENT_NAME_PURCHASE,
  G4_EVENT_NAME_SEND_NBQ,
  G4_EVENT_NAME_SEND_QUOTE
} from '~~/lib/constants';
import {
  mapCartItemsForAnalytics,
  pickAnalyticsCartItemFields
} from '~/stores/cartStore/helper/mapCartItemsForAnalytics';
import type { AnalyticsCartItemSource } from '~/stores/cartStore/model';
import type { EnrichedCartFragment } from '~~/server/utils/enrichCartWithProductBreadcrumbs';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import { extractError } from '~/utils/extractError';
import type { ConfirmationActionModel } from '~/stores/confirmationStore/models';
import { cloneDeep } from '~/utils/cloneDeep';
import { getCountryByCode } from '~/utils/countries';
import type { CustomerOrdersResponse } from '~/stores/customerAccountStore/models';

export type ConfirmationType = 'request' | 'order';
export enum ConfirmationTypes {
  REQUEST = 'request',
  ORDER = 'order'
}

export type ExtendedProduct = (CartItemFragment | ProductInterface) & {
  product: CartItemFragment | ProductInterface;
  quantity: number;
  decoratedProduct: DecoratedProduct;
  breadcrumbs?: AnalyticsCartItemSource['breadcrumbs'];
};

function mapCartProductsFromCart(
  cart: EnrichedCartFragment | null,
  textDictionary: Record<string, string>
): ExtendedProduct[] {
  const itemBreadcrumbs = cart?.itemBreadcrumbs ?? {};

  return (
    cloneDeep(cart?.itemsV2?.items ?? [])
      .filter(isDefined)
      .map((item) => {
        const product = item.product as unknown as CommonProductFragment;

        return {
          ...item,
          decoratedProduct: DecoratedProduct.build(product, textDictionary),
          breadcrumbs: item.uid ? itemBreadcrumbs[item.uid] : undefined
        } as unknown as ExtendedProduct;
      }) ?? []
  );
}

export const useConfirmationStore = defineStore('shop-confirmation', () => {
  const type = ref<ConfirmationType | null>(null);
  const orderNumber = ref<string | null>(null);
  const email = ref<string | null>(null);
  const shippingMethod = ref<SelectedShippingMethodFragment | null>(null);
  const loading = ref<boolean>(false);
  const lastAction = ref<ConfirmationActionModel | null>(null);
  const lastOrder = ref<CustomerOrder | null>(null);
  const products = ref<ReadonlyArray<ExtendedProduct>>([]);
  const prices = ref<CartFragment['prices'] | OrderTotal | null>(null);
  const shippingAddress = ref<
    ShippingCartAddressFragment | Omit<OrderAddress, '__typename'> | null
  >(null);
  const billingAddress = ref<BillingCartAddressFragment | OrderAddress | null>(
    null
  );
  const paymentMethod = ref<SelectedPaymentMethod | null>(null);
  const cartId = ref<string | null>(null);
  /** Signed PDF download URL of the placed NBQ (Angebots-PDF). */
  const nbqPdfUrl = ref<string | null>(null);

  const route = useRoute();
  const cartStore = useCartStore();
  const { user } = useShopAuth();
  const {
    $globalPageSettings,
    $analytics,
    $activeLocale,
    $textDictionary,
    $shopStore
  } = useNuxtApp();

  const _state = {
    type,
    orderNumber,
    email,
    shippingMethod,
    products,
    prices,
    shippingAddress,
    billingAddress,
    paymentMethod
  };

  const initByOrder = (
    givenOrderNumber: string,
    cart: EnrichedCartFragment | null
  ) => {
    type.value = ConfirmationTypes.ORDER;
    orderNumber.value = givenOrderNumber;
    email.value = (cart?.email || user.value?.userInfo?.email || null) as
      string | null;
    shippingMethod.value =
      cloneDeep(cart?.shipping_addresses[0]?.selected_shipping_method) ?? null;
    products.value = mapCartProductsFromCart(cart, $textDictionary.value);
    prices.value = cloneDeep(cart?.prices) ?? null;
    shippingAddress.value = cloneDeep(cart?.shipping_addresses[0]) ?? null;
    billingAddress.value = cloneDeep(cart?.billing_address) ?? null;
    paymentMethod.value = cloneDeep(cart?.selected_payment_method) ?? null;
    cartId.value = cart?.id ?? null;
    nbqPdfUrl.value = null;

    // Reset the guard when initializing a new order (different order number)
    if (processedOrderNumber.value !== givenOrderNumber) {
      processedOrderNumber.value = null;
      isProcessingOrder.value = false;
    }
  };

  const initByQuote = (
    cart: EnrichedCartFragment | null,
    quoteNumber: string,
    input: PlaceQuoteInput
  ) => {
    type.value = ConfirmationTypes.REQUEST;
    orderNumber.value = quoteNumber;
    email.value = input.email ?? null;
    shippingMethod.value = null;
    products.value = mapCartProductsFromCart(cart, $textDictionary.value);
    prices.value = cloneDeep(cart?.prices) ?? null;
    cartId.value = cart?.id ?? input.cart_id ?? null;

    // TODO: Better would be to use a normalized Address Type here
    // Magento PlaceQuoteInput address shape is incomplete vs cart/order address unions.
    shippingAddress.value = {
      city: input.city ?? '',
      company: input.company ?? '',
      country: {
        code: input.country_id ?? '',
        label: getCountryByCode(input.country_id ?? '')?.countryName ?? ''
      },
      region: { code: input.region_id ?? '', label: null },
      middlename: input.firstname + ' ' + input.lastname,
      street: [input.street ?? ''],
      postcode: input.postcode ?? '',
      telephone: input.telephone ?? '',
      same_as_billing: false
    } as unknown as Omit<OrderAddress, '__typename'>;
    billingAddress.value = null;
    paymentMethod.value = null;
    nbqPdfUrl.value = null;

    // Reset the guard when initializing a new quote (different quote number)
    if (processedQuoteNumber.value !== quoteNumber) {
      processedQuoteNumber.value = null;
      isProcessingQuote.value = false;
    }
  };

  /**
   * Initializes the confirmation store for an NBQ (Non-Binding Quote).
   * Unlike `initByQuote`, the addresses are taken directly from the cart
   * (the NBQ checkout sets proper billing/shipping addresses on the cart),
   * not from a synthetic PlaceQuoteInput.
   */
  const initByNBQ = (
    cart: EnrichedCartFragment | null,
    nbqNumber: string,
    options?: { pdfUrl?: string | null }
  ) => {
    type.value = ConfirmationTypes.REQUEST;
    orderNumber.value = nbqNumber;
    email.value = (cart?.email || user.value?.userInfo?.email || null) as
      string | null;
    shippingMethod.value =
      cloneDeep(cart?.shipping_addresses?.[0]?.selected_shipping_method) ??
      null;
    products.value = mapCartProductsFromCart(cart, $textDictionary.value);
    prices.value = cloneDeep(cart?.prices) ?? null;
    shippingAddress.value = cloneDeep(cart?.shipping_addresses?.[0]) ?? null;
    billingAddress.value = cloneDeep(cart?.billing_address) ?? null;
    paymentMethod.value = null;
    cartId.value = cart?.id ?? null;
    nbqPdfUrl.value = options?.pdfUrl ?? null;

    if (processedNBQNumber.value !== nbqNumber) {
      processedNBQNumber.value = null;
      isProcessingNBQ.value = false;
    }
  };

  const loadLastOrder = async () => {
    loading.value = true;
    const action: ConfirmationActionModel = {
      type: 'loadLastOrder'
    };

    try {
      const res =
        (await $fetch<CustomerOrdersResponse | null>(
          '/api/magento/customer/orders/',
          {
            ...DEFAULT_FETCH_OPTIONS,
            query: {
              type: 'customer',
              locale: $activeLocale.value.webapp,
              store: $shopStore.value,
              pageSize: 1,
              variables: {}
            }
          }
        )) ?? null;

      if (isDefined(res?.items?.[0])) {
        const order: CustomerOrder = res.items[0] as CustomerOrder;

        type.value = ConfirmationTypes.ORDER;
        orderNumber.value = order.number ?? null;
        email.value = (order.email || user.value?.userInfo?.email || null) as
          string | null;
        shippingMethod.value = order.shipping_method
          ? ({
              carrier_code: '',
              method_code: '',
              method_title: '',
              carrier_title: order.shipping_method,
              amount: order.total?.total_shipping
            } as SelectedShippingMethodFragment)
          : null;
        products.value =
          (order.items ?? [])
            .filter(isDefined)
            .map((item) => {
              if (!isDefined(item.product)) {
                return;
              }
              return {
                product: item.product as ProductInterface,
                quantity: item.quantity_ordered ?? 1,
                decoratedProduct: DecoratedProduct.build(
                  item.product as unknown as ProductDetailFragment,
                  $textDictionary.value
                )
              } as ExtendedProduct;
            })
            .filter(isDefined) ?? [];

        prices.value = order?.total ?? null;
        shippingAddress.value = order.shipping_address ?? null;
        billingAddress.value = order.billing_address ?? null;
        paymentMethod.value = order?.payment_methods?.[0]
          ? ({
              title: order.payment_methods?.[0].name ?? '',
              code: order.payment_methods?.[0].type ?? ''
            } as SelectedPaymentMethod)
          : null;
        cartId.value = null;

        // Reset the guard when initializing a new order (different order number)
        if (processedOrderNumber.value !== orderNumber.value) {
          processedOrderNumber.value = null;
          isProcessingOrder.value = false;
        }
      } else {
        lastOrder.value = null;
      }
    } catch (e) {
      action.error = extractError(e);
    } finally {
      lastAction.value = action;
      loading.value = false;
    }
  };

  const appliedTaxes = computed(() => {
    if (!isDefined(prices.value)) {
      return [];
    }

    if ('applied_taxes' in prices.value) {
      return filterAppliedTaxRows(prices.value.applied_taxes);
    }

    if ('taxes' in prices.value) {
      return filterAppliedTaxRows(prices.value.taxes);
    }

    return [];
  });

  const tax: ComputedRef<Money> = computed(
    () => sumAppliedTaxAmounts(appliedTaxes.value) as Money
  );

  const subTotalExcludingTax = computed(() => {
    if (isDefined(prices.value)) {
      if ('subtotal_excluding_tax' in prices.value) {
        return prices.value.subtotal_excluding_tax;
      }

      if ('subtotal_excl_tax' in prices.value) {
        return prices.value.subtotal_excl_tax;
      }
    }
  });

  const hasTax = computed(() => appliedTaxes.value.length > 0);

  const hasTariff = computed(() => {
    return prices.value?.fooman_applied_surcharges?.length ?? false;
  });

  const tariff = computed(() => {
    return prices.value?.fooman_applied_surcharges?.[0]?.amount;
  });

  // Guard to prevent multiple executions of afterPayedOrder
  const isProcessingOrder = ref(false);
  const processedOrderNumber = ref<string | null>(null);

  // Guard to prevent multiple executions of afterSendQuote
  const isProcessingQuote = ref(false);
  const processedQuoteNumber = ref<string | null>(null);

  // Guard to prevent multiple executions of afterSendNBQ
  const isProcessingNBQ = ref(false);
  const processedNBQNumber = ref<string | null>(null);

  /**
   * True after a successful NBQ submission, until the next regular quote/order.
   * Used by RequestConfirmation.vue to render NBQ-specific copy instead of
   * relying on the locale, which would affect regular quote confirmations too.
   */
  const isNBQConfirmation = ref(false);

  const afterPayedOrder = async function () {
    isNBQConfirmation.value = false;
    // Prevent multiple concurrent executions
    if (isProcessingOrder.value) {
      return;
    }

    // Prevent duplicate execution for the same order
    if (
      isDefined(orderNumber.value) &&
      processedOrderNumber.value === orderNumber.value
    ) {
      return;
    }

    // Validate that we have required data before proceeding
    if (!isDefined(orderNumber.value) || products.value.length === 0) {
      return;
    }

    isProcessingOrder.value = true;
    processedOrderNumber.value = orderNumber.value;

    try {
      const confirmationPageSlug =
        $globalPageSettings.value?.shopConfig?.cartConfirmationPage?.slug;

      // Await the async function to get the actual data instead of a Promise
      const ecommerceData = await createEcommerceTrackingData({
        cartItems: mapCartItemsForAnalytics(
          products.value.map((item) =>
            pickAnalyticsCartItemFields(
              item as unknown as AnalyticsCartItemSource
            )
          )
        ),
        tax: tax.value ?? undefined,
        coupon: prices.value?.discounts?.[0]?.coupon?.code ?? undefined,
        transactionId: orderNumber.value ?? undefined
        // Do not pass shipping (address) or cartId: GA4 purchase only allows
        // currency, value, transaction_id, coupon, shipping (number), tax (number), items.
      });

      // Only push to dataLayer if we have valid ecommerce data. Build GA4-compliant
      // purchase payload: no address data, shipping/tax as numbers only, items with GA4-allowed fields only.
      if (ecommerceData) {
        const taxAmount = tax.value.value ?? 0;
        const shippingCost = shippingMethod.value?.amount?.value ?? 0;
        const GA4_ITEM_KEYS = [
          'item_id',
          'item_name',
          'affiliation',
          'coupon',
          'discount',
          'index',
          'item_brand',
          'item_category',
          'item_category2',
          'item_category3',
          'item_category4',
          'item_category5',
          'item_list_id',
          'item_list_name',
          'item_variant',
          'location_id',
          'price',
          'quantity'
        ] as const;
        const itemsGa4 = (ecommerceData.items ?? []).map(
          (item: Record<string, unknown>, index: number) => {
            const out: Record<string, unknown> = { index };
            for (const key of GA4_ITEM_KEYS) {
              if (key !== 'index' && key in item && item[key] != null) {
                out[key] = item[key];
              }
            }
            return out;
          }
        );
        const purchaseEcommerce: Record<string, unknown> = {
          currency: ecommerceData.currency,
          value: ecommerceData.value,
          transaction_id: ecommerceData.transaction_id,
          shipping: typeof shippingCost === 'number' ? shippingCost : 0,
          tax: typeof taxAmount === 'number' ? taxAmount : 0,
          items: itemsGa4
        };
        if (ecommerceData.coupon) {
          purchaseEcommerce.coupon = ecommerceData.coupon;
        }
        $analytics.pushToDataLayer({
          event: G4_EVENT_NAME_PURCHASE,
          userId: user?.value?.claims?.oid?.toString() ?? 'null',
          ecommerce: purchaseEcommerce
        });
      }

      cartStore.onOrderCompleted();

      if (confirmationPageSlug) {
        await enhancedNavigateTo(
          {
            path: buildUrlString(
              $activeLocale.value.webapp,
              confirmationPageSlug
            ),
            query: route.query
          },
          {
            external: false,
            replace: false
          }
        );
      }
    } finally {
      isProcessingOrder.value = false;
    }
  };

  const afterSendQuote = async function () {
    isNBQConfirmation.value = false;
    // Prevent multiple concurrent executions
    if (isProcessingQuote.value) {
      return;
    }

    // Prevent duplicate execution for the same quote
    if (
      isDefined(orderNumber.value) &&
      processedQuoteNumber.value === orderNumber.value
    ) {
      return;
    }

    // Validate that we have required data before proceeding
    if (!isDefined(orderNumber.value) || products.value.length === 0) {
      return;
    }

    isProcessingQuote.value = true;
    processedQuoteNumber.value = orderNumber.value;

    try {
      const confirmationPageSlug =
        $globalPageSettings.value?.shopConfig?.requestCartConfirmationPage
          ?.slug;

      // Await the async function to get the actual data instead of a Promise
      const ecommerceData = await createEcommerceTrackingData({
        cartItems: mapCartItemsForAnalytics(
          products.value.map((item) =>
            pickAnalyticsCartItemFields(
              item as unknown as AnalyticsCartItemSource
            )
          )
        ),
        tax: tax.value ?? undefined,
        coupon: prices.value?.discounts?.[0]?.coupon?.code ?? undefined,
        transactionId: orderNumber.value ?? undefined,
        cartId: cartId.value ?? undefined
        // Do not pass shipping (address) or cartId: GA4 purchase only allows
        // currency, value, transaction_id, coupon, shipping (number), tax (number), items.
      });

      // Only push to dataLayer if we have valid ecommerce data
      if (ecommerceData) {
        $analytics.pushToDataLayer({
          event: G4_EVENT_NAME_SEND_QUOTE,
          userId: user?.value?.claims?.oid?.toString() ?? 'null',
          ecommerce: ecommerceData
        });
      }

      cartStore.onQuoteCompleted();

      if (confirmationPageSlug) {
        await enhancedNavigateTo(
          {
            path: buildUrlString(
              $activeLocale.value.webapp,
              confirmationPageSlug
            ),
            query: route.query
          },
          {
            external: false,
            replace: false
          }
        );
      }
    } finally {
      isProcessingQuote.value = false;
    }
  };

  /**
   * Called after a successful NBQ (Non-Binding Quote / Angebots-PDF) submission.
   * Mirrors `afterSendQuote` but fires the NBQ-specific analytics event and
   * navigates to the same `requestCartConfirmationPage` slug (no new slug needed).
   */
  const afterSendNBQ = async function () {
    if (isProcessingNBQ.value) {
      return;
    }

    if (!isDefined(orderNumber.value) || products.value.length === 0) {
      return;
    }

    if (processedNBQNumber.value === orderNumber.value) {
      return;
    }

    isNBQConfirmation.value = true;
    isProcessingNBQ.value = true;
    processedNBQNumber.value = orderNumber.value;

    try {
      const confirmationPageSlug =
        $globalPageSettings.value?.shopConfig?.requestCartConfirmationPage
          ?.slug;

      const ecommerceData = await createEcommerceTrackingData({
        cartItems: mapCartItemsForAnalytics(
          products.value.map((item) =>
            pickAnalyticsCartItemFields(
              item as unknown as AnalyticsCartItemSource
            )
          )
        ),
        transactionId: orderNumber.value ?? undefined,
        cartId: cartId.value ?? undefined
      });

      if (ecommerceData) {
        $analytics.pushToDataLayer({
          event: G4_EVENT_NAME_SEND_NBQ,
          userId: user?.value?.claims?.oid?.toString() ?? 'null',
          ecommerce: ecommerceData
        });
      }

      cartStore.onQuoteCompleted();

      if (confirmationPageSlug) {
        await enhancedNavigateTo(
          {
            path: buildUrlString(
              $activeLocale.value.webapp,
              confirmationPageSlug
            ),
            query: route.query
          },
          {
            external: false,
            replace: false
          }
        );
      }
    } finally {
      isProcessingNBQ.value = false;
    }
  };

  const isValid = computed(() => {
    return products.value.length && isDefined(orderNumber.value);
  });

  const shippingEqualsBilling = computed(() => {
    const shipping = shippingAddress.value;
    const billing = billingAddress.value;

    if (!isDefined(shipping) || !isDefined(billing)) {
      return false;
    }

    if ('same_as_billing' in shipping && shipping.same_as_billing === true) {
      return true;
    }

    if (
      'id' in shipping &&
      'id' in billing &&
      isDefined(shipping.id) &&
      isDefined(billing.id)
    ) {
      return shipping.id === billing.id;
    }

    return false;
  });

  const afterRequestCompleted = async function () {
    // TODO: implement
  };

  return {
    _state,
    type,
    orderNumber,
    email,
    shippingMethod,
    products,
    prices,
    subTotalExcludingTax,
    shippingAddress,
    billingAddress,
    shippingEqualsBilling,
    paymentMethod,
    hasTax,
    tax,
    appliedTaxes,
    hasTariff,
    tariff,
    loadLastOrder,
    isValid,

    initByOrder,
    afterPayedOrder,
    afterRequestCompleted,

    initByQuote,
    afterSendQuote,
    initByNBQ,
    afterSendNBQ,
    isNBQConfirmation,
    nbqPdfUrl
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useConfirmationStore, import.meta.hot)
  );
}
